export type ChipDef = {
  emoji: string;
  label: string;
  prompt: string;
  /** Secondary prompt shown to free users for Pro-gated chips. */
  promptFree?: string;
  /** When true, renders a "⚡ Kin Pro" badge overlay on the chip card. */
  isProFeature?: boolean;
  /**
   * Inclusive [startMonth, endMonth] window, 1 = January … 12 = December.
   * A chip with no `months` is in season every month — this is metadata a
   * chip opts into, never something every chip has to declare.
   * Windows may wrap the year end: [11, 1] means Nov, Dec, Jan.
   * Resolved against the user's LOCAL month by `isChipInSeason` — see the
   * note there about why this must not become a UTC boundary.
   */
  months?: [number, number];
};

/**
 * Is this chip in season right now? (Issue #593)
 *
 * Pure and side-effect-free, in the same spirit as `capLogic.ts` and
 * `updateBarParser.ts` — `now` is always passed in by the caller so the whole
 * year is unit-testable without mocking the clock.
 *
 * A chip with no `months` window is ALWAYS in season, so this is safe to run
 * over every chip in every category rather than special-casing one.
 *
 * ⚠️ Month resolution is LOCAL (`getMonth()`), deliberately — consistent with
 * the rest of the app (see `user_cap_day` / the DAU local-day work). Do not
 * "fix" this to `getUTCMonth()`: for a mom in Hawaii that would flip her
 * seasonal chips over on the afternoon of the last day of the month.
 */
export function isChipInSeason(
  chip: Pick<ChipDef, "months"> | null | undefined,
  now: Date = new Date()
): boolean {
  if (!chip || !chip.months) return true;
  const [start, end] = chip.months;
  const month = now.getMonth() + 1; // 1-12, local
  // Wrapping window (e.g. Nov–Jan) is an OR, not an AND.
  return start <= end
    ? month >= start && month <= end
    : month >= start || month <= end;
}

/**
 * Gift Tracker chip — 🎄 (deliberately a tree, not 🎁, to stay visually
 * distinct from the existing "Gift ideas" brainstorm chip which keeps 🎁).
 * Single static prompt (no rotation) — registered in ROTATING_PROMPTS as a
 * one-element array so the tap-time lookup and the ChipDef.prompt agree.
 */
const GIFT_TRACKER_LABEL = "Gift list tracker";
const GIFT_TRACKER_PROMPT =
  "I want to keep track of the gifts I'm buying — who they're for, what I'm getting, and what I'm actually spending. Help me get it all in one place before I lose track.";

export function getGiftTrackerChip(): ChipDef {
  return { emoji: "🎄", label: GIFT_TRACKER_LABEL, prompt: GIFT_TRACKER_PROMPT };
}

/**
 * Labels with multiple rotating prompts. On each tap, resolveChipPrompt(label)
 * returns one of the prompts at random. The visible label/emoji never changes.
 */
const ROTATING_PROMPTS: Record<string, string[]> = {
  [GIFT_TRACKER_LABEL]: [GIFT_TRACKER_PROMPT],
  "Meal plan for picky eaters": [
    "I need a meal plan for the week. Picky eaters and not a lot of time.",
    "My kids won't eat anything and I'm out of dinner ideas. Help.",
    "Can you give me a weekly meal plan that's fast and kid-approved?",
  ],
  "Plan a family trip": [
    "Help me plan a family-friendly trip on a real budget.",
    "I want to plan a trip my kids will love without losing my mind.",
    "Help me plan a family vacation. Where should we even go?",
  ],
  "Write a work email": [
    "I need to write a work email. Help me sound professional but human.",
    "Help me write a follow-up email to someone who hasn't responded.",
    "Draft an email to my boss about a project update.",
  ],
  "Garden planning": [
    "I want to start a garden but I have no idea where to begin.",
    "Help me figure out what I should be planting right now.",
    "I want to grow my own food this year. What do I do first?",
  ],
  "AI 101, where do I start?": [
    "I keep hearing about AI but I have no idea where to start.",
    "What are the top 3 things I should try with AI this week?",
    "I downloaded ChatGPT and now I'm just staring at it. What do I actually do?",
  ],
  "Prep for a big meeting": [
    "I have a big meeting tomorrow and I need to show up sharp.",
    "Help me prep for a meeting where I'm presenting my team's results.",
    "I have a 1:1 with my skip-level this week. Help me prep.",
  ],
  "Brain dump": [
    "My brain is full. Let me dump it all and help me sort it after.",
    "I have a million things in my head and I can't focus. Help me unload.",
    "I need to get everything out of my head right now.",
  ],
  "Handle a hard convo": [
    "I need to have a hard conversation and I don't know how to start it.",
    "There's a conversation I've been avoiding. Help me plan what to say.",
    "I need to say something difficult and I keep chickening out.",
  ],
  "Pack for the weekend": [
    "We're going away for the weekend. Help me pack so I don't forget anything.",
    "Weekend trip coming up. Help me build a packing list.",
    "Help me pack for the whole family for a weekend away.",
  ],
  "School week prep": [
    "Help me get ready for the school week.",
    "Help me figure out what I need to prep for this school week.",
    "I need a game plan for the school week.",
  ],
  "Content for my brand": [
    "Help me plan content for my brand this week.",
    "My content calendar is empty. Help me brainstorm what to post.",
    "I need content ideas that actually sound like me.",
  ],
  "Say something nice": [
    "Can you just say something kind to me? No advice.",
    "Today has been a lot. Remind me I'm doing a good job.",
    "I'm running on empty. Just say something nice.",
  ],
  "I need to vent": [
    "I just need to vent. Don't try to fix it, just listen.",
    "I need to talk. No solutions yet. Just listen for a minute.",
    "Everything is annoying me right now. Let me go.",
  ],
  "Lunch ideas for the week": [
    "I need easy lunch ideas my kids will actually eat.",
    "I'm tired of making the same lunches. Give me fresh ideas.",
    "Help me plan school lunches for the week.",
  ],
  "Gift ideas": [
    "I need gift ideas for someone and I have no clue what to get them.",
    "Help me find the perfect gift. I'll tell you about the person.",
    "I'm stuck on a gift idea. Can you help me brainstorm?",
  ],
"Back-to-school game plan": [
    "School starts soon and my brain is everywhere. Walk me through a back-to-school game plan: what to buy, what to schedule, what to prep the week before — one step at a time so it doesn't feel like a mountain.",
    "Help me make a back-to-school checklist for my kids — supplies, clothes that fit, forms, schedules. Ask me their ages first so it's actually right.",
    "First week of school survival mode: help me plan lunches, outfits, and the morning routine so nobody cries before 8am. Including me.",
  ],
  "Summer camps & childcare": [
    "I need to figure out summer childcare. Help me plan out camps, coverage, and who has the kids each week.",
    "Summer is coming and I have zero plan for the kids. Help me think through camp options, schedule gaps, and backup plans.",
    "Can you help me map out a summer schedule? I need to figure out camps, grandparent weeks, and what to do about the gaps.",
  ],
  "Summer bucket list": [
    "Help me make a summer bucket list for my kids. Fun stuff, free stuff, adventures, and things we can actually do.",
    "I want to plan a summer bucket list the kids will actually remember. Mix of big adventures and easy at-home stuff.",
    "Can you help me come up with a summer bucket list? I want a mix of outdoor activities, creative projects, and day trips.",
  ],
  "Why won't baby sleep": [
    "Help me figure out what's going on with baby's sleep. Ask me how old baby is and when baby last ate before giving advice.",
    "Help me figure out what's going on with baby's sleep. Ask me baby's age and last feed first, then we'll go from there.",
    "Sleep is falling apart. Don't give me a sleep training plan unless I ask. Just help me figure out what might be off tonight.",
  ],
  "Feeding troubleshooting": [
    "Something feels off with feeding and I need help thinking it through. Ask me one thing first so you have context.",
    "Help me troubleshoot feeding. Don't assume breast or bottle. Ask the one question that matters most and we'll go from there.",
    "Feeding isn't going well today. Ask me one diagnostic question before you say anything else.",
  ],
  "Should I worry about this": [
    "Something with baby is making me worry and I need a calm second opinion. Tell me what to monitor and when to call the pediatrician. Don't try to diagnose.",
    "Is this something to worry about? Help me figure out what to monitor and when it's a doctor call. You're not diagnosing.",
    "Walk me through what to keep an eye on with this and when I should actually call the pediatrician.",
  ],
  "Plan my day around naps": [
    "Help me plan a realistic day around baby's naps. Ask me what baby's nap schedule looks like before suggesting anything.",
    "I want a daily structure that actually works around naps. Ask me what baby's nap schedule looks like before you build it.",
    "Help me figure out a doable day. Ask me baby's current nap schedule first, then we'll work around it.",
  ],
  "Worth buying for baby?": [
    "Cut through the Instagram-fueled overconsumption. What baby items do I actually need versus what's marketed to me?",
    "I keep getting ads for baby stuff. Help me figure out what's genuinely useful vs. what I can skip.",
    "Be honest about baby gear. What's worth it, what's overhyped, and what can wait?",
  ],
  "Quick one-handed meal": [
    "I need a quick dinner I can eat one-handed while holding baby. Something real, not a granola bar.",
    "Need a dinner I can actually eat with one arm full of baby. Keep it doable.",
    "What's a quick dinner I can make and eat one-handed tonight?",
  ],
  "Freezer meal prep": [
    "Help me plan freezer meals I can prep this weekend so I have real food to reheat next week.",
    "I want to stock the freezer this weekend. Give me a short list of meals that reheat well.",
    "Walk me through 3-4 freezer meals I can batch this weekend without losing my whole Saturday.",
  ],
  "One-handed dinner": [
    "Tell me what to make for dinner tonight that I can prep and eat one-handed. Bonus if it uses what's already in my fridge.",
    "Help me figure out dinner tonight. Has to be one-handed friendly.",
    "I have baby in one arm. What can I cook and eat tonight?",
  ],
  "Is AI actually useful?": [
    "I'm skeptical AI is actually useful for moms like me. Prove it: ask me 3 quick questions about my actual week, then show me one real thing you can take off my plate today.",
    "People say you get out what you put in with AI. Teach me the one trick that makes your answers actually good — then let me try it on something real from my life.",
    "Show me the difference between a lazy question and a good one. Take something moms ask you all the time, answer it both ways, and teach me why the second one works better.",
  ],
};

/**
 * Seasonal chips — the ONE part of the chip set whose job is to prove Kin
 * knows what month it is (issue #593). Each carries an inclusive `months`
 * window; out-of-season chips are filtered at read time by
 * `getRandomSeasonalChip` and `getSeeMoreChips`, never by a copied list.
 *
 * Windows are deliberately keyed to when a mom PLANS the thing, not when it
 * happens: camp registration fills up in late winter, so that chip has to be
 * on screen in February, not in July when every spot is gone.
 *
 * Oct–Jan currently has no seasonal chip, so the Seasonal category simply
 * does not render those months. That is intentional and better than a stale
 * summer chip — filling those months out is its own issue, not this one.
 */
const SEASONAL_CHIPS: ChipDef[] = [
  { emoji: "🪣", label: "Summer bucket list", prompt: ROTATING_PROMPTS["Summer bucket list"][0], months: [5, 8] },
  { emoji: "☀️", label: "Summer camps & childcare", prompt: ROTATING_PROMPTS["Summer camps & childcare"][0], months: [2, 6] },
  { emoji: "🎒", label: "Back-to-school game plan", prompt: ROTATING_PROMPTS["Back-to-school game plan"][0], months: [7, 9] },
];

/** Reno helper rotation prompts (label rotates separately via RENO_HELPER_LABELS). */
const RENO_HELPER_ROTATING_PROMPTS = [
  "I'm planning a bathroom renovation. Help me figure out a budget.",
  "I want to redo my kitchen but I have no idea where to start.",
  "Help me prioritize which home projects to tackle first.",
];

/** Event planner rotation prompts (label rotates separately via EVENT_PLANNER_LABELS). */
const EVENT_PLANNER_ROTATING_PROMPTS = [
  "I need to plan a party or shower and I don't know where to start.",
  "I'm in charge of planning an event and I need a game plan.",
  "I have to throw a shower or party. Help me plan it step by step.",
];

function pickRandom<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Given a chip's label and its static prompt, return the prompt to send.
 * For labels in ROTATING_PROMPTS (or rotating reno/event sets), pick a random
 * rotation per call. Otherwise return the static prompt unchanged.
 * Special action prompts (e.g. __OPEN_FRIDGE_SNAP__) always pass through as-is.
 */
export function resolveChipPrompt(label: string, fallbackPrompt: string): string {
  if (fallbackPrompt.startsWith("__")) return fallbackPrompt;
  if (ROTATING_PROMPTS[label]) return pickRandom(ROTATING_PROMPTS[label]);
  if (RENO_HELPER_LABEL_SET.has(label)) return pickRandom(RENO_HELPER_ROTATING_PROMPTS);
  if (EVENT_PLANNER_LABEL_SET.has(label)) return pickRandom(EVENT_PLANNER_ROTATING_PROMPTS);
  return fallbackPrompt;
}

export const MEAL_CHIPS: ChipDef[] = [
  {
    emoji: "🍽️",
    label: "Meal plan for picky eaters",
    prompt: "I need a meal plan for the week. Picky eaters and not a lot of time.",
  },
  {
    emoji: "🥣",
    label: "Meal prep for the week",
    prompt: "Help me meal prep for the week. Keep it doable.",
  },
  {
    emoji: "🛒",
    label: "What's for dinner tonight",
    prompt: "I have 30 minutes and picky kids. What's for dinner tonight?",
  },
  {
    emoji: "📸",
    label: "Snap your fridge or pantry",
    prompt: "__OPEN_FRIDGE_SNAP__",
  },
];

export const NEW_MOM_MEAL_CHIPS: ChipDef[] = [
  {
    emoji: "🍽️",
    label: "Quick one-handed meal",
    prompt: "I need a quick dinner I can eat one-handed while holding baby. Something real, not a granola bar.",
  },
  {
    emoji: "🥣",
    label: "Freezer meal prep",
    prompt: "Help me plan freezer meals I can prep this weekend so I have real food to reheat next week.",
  },
  {
    emoji: "🛒",
    label: "One-handed dinner",
    prompt: "Tell me what to make for dinner tonight that I can prep and eat one-handed. Bonus if it uses what's already in my fridge.",
  },
  {
    emoji: "📸",
    label: "Snap your fridge or pantry",
    prompt: "__OPEN_FRIDGE_SNAP__",
  },
];

/**
 * Reno project helper chip — single static label, prompt rotates via ROTATING_PROMPTS-style logic.
 */
const RENO_HELPER_LABEL = "Reno project helper";
const RENO_HELPER_PROMPT = RENO_HELPER_ROTATING_PROMPTS[0];

export function getRenoHelperChip(): ChipDef {
  return { emoji: "🔨", label: RENO_HELPER_LABEL, prompt: RENO_HELPER_PROMPT };
}

/** Set of all possible reno helper labels — used for matching. */
export const RENO_HELPER_LABEL_SET: ReadonlySet<string> = new Set([RENO_HELPER_LABEL]);

/**
 * Event planner chip — single static label, prompt rotates via ROTATING_PROMPTS-style logic.
 */
const EVENT_PLANNER_LABEL = "Plan a party or shower";
const EVENT_PLANNER_PROMPT = EVENT_PLANNER_ROTATING_PROMPTS[0];

export function getEventPlannerChip(): ChipDef {
  return { emoji: "🎉", label: EVENT_PLANNER_LABEL, prompt: EVENT_PLANNER_PROMPT };
}

/** Set of all possible event planner labels — used for matching. */
export const EVENT_PLANNER_LABEL_SET: ReadonlySet<string> = new Set([EVENT_PLANNER_LABEL]);

/**
 * "How do I make this?" photo chip — snap a pic of any dish, craft, or Pinterest
 * find and Kin explains how to recreate it at home. Lives only in the See more
 * "Quick Wins" category (not in any main pool). Opens PhotoIntakeModal via the
 * __OPEN_HOW_TO_MAKE__ sentinel, same Pattern B flow as "What's in this?".
 */
const HOW_TO_MAKE_LABEL = "How do I make this?";

export function getHowToMakeChip(): ChipDef {
  return { emoji: "📸", label: HOW_TO_MAKE_LABEL, prompt: "__OPEN_HOW_TO_MAKE__" };
}


export const DEFAULT_CHIPS: ChipDef[] = [
  getGiftTrackerChip(),
  { emoji: "🔍", label: "What's in this?", prompt: "__OPEN_WHATS_IN_THIS__" },
  { emoji: "📋", label: "Snap a sign-up sheet", prompt: "__OPEN_SIGNUP_SNAP__" },
  { emoji: "🩹", label: "Snap a rash", prompt: "__OPEN_RASH_SNAP__" },
  { emoji: "🎁", label: "Gift ideas", prompt: "I need gift ideas for someone and I have no clue what to get them." },
  { emoji: "📧", label: "Write a work email", prompt: "I need to write a work email. Help me sound professional but human." },
  { emoji: "📋", label: "Budget tracker", prompt: "I'm planning a project and I need to budget for it. Help me figure it out." },
  getEventPlannerChip(),
  { emoji: "💼", label: "Prep for a big meeting", prompt: "I have a big meeting coming up and I need to show up sharp." },
  { emoji: "🌱", label: "Garden planning", prompt: "I want to start a garden but I have no idea where to begin." },
  { emoji: "🤖", label: "AI 101, where do I start?", prompt: "I keep hearing about AI but I have no idea where to start." },
  { emoji: "✈️", label: "Plan a family trip", prompt: "Help me plan a family-friendly trip on a real budget." },
];

export const MOM_TYPE_POOLS: Record<string, ChipDef[]> = {
  "Corporate Mom": [
    getGiftTrackerChip(),
    { emoji: "💼", label: "Prep for a big meeting", prompt: "I have a big meeting coming up and I need to show up sharp." },
    { emoji: "📧", label: "Write a work email", prompt: "I need to write a work email. Help me sound professional but human." },
    { emoji: "🤝", label: "Handle a hard convo", prompt: "I have a difficult work conversation coming up. Help me prep." },
    { emoji: "📊", label: "Make me look smart", prompt: "I have to present something at work and I want it to land." },
    { emoji: "💬", label: "Manager meeting prep", prompt: "I have a meeting with my manager. Help me prep." },
    { emoji: "🔍", label: "What's in this?", prompt: "__OPEN_WHATS_IN_THIS__" },
    { emoji: "📋", label: "Snap a sign-up sheet", prompt: "__OPEN_SIGNUP_SNAP__" },
    { emoji: "🩹", label: "Snap a rash", prompt: "__OPEN_RASH_SNAP__" },
    getEventPlannerChip(),
  ],
  "Creative / Side Hustle Mom": [
    getGiftTrackerChip(),
    { emoji: "🎨", label: "Restart my side project", prompt: "I had a creative side project before kids and I want to get back to it." },
    { emoji: "🛍️", label: "Write product descriptions", prompt: "I'm writing product descriptions for my shop. Help me make them sound like me." },
    { emoji: "💡", label: "Pick a business idea", prompt: "I have a few business ideas. Help me actually pick one." },
    { emoji: "📝", label: "Pitch to a client", prompt: "I need to pitch a freelance client. Help me write something confident." },
    { emoji: "🚀", label: "Launch something this week", prompt: "Help me actually ship something small this week." },
    { emoji: "📱", label: "Content for my brand", prompt: "I need content ideas for my brand." },
    { emoji: "🔍", label: "What's in this?", prompt: "__OPEN_WHATS_IN_THIS__" },
    { emoji: "📋", label: "Snap a sign-up sheet", prompt: "__OPEN_SIGNUP_SNAP__" },
    { emoji: "🩹", label: "Snap a rash", prompt: "__OPEN_RASH_SNAP__" },
    getEventPlannerChip(),
  ],
  "All-the-Things Mom": [
    getGiftTrackerChip(),
    { emoji: "🔍", label: "What's in this?", prompt: "__OPEN_WHATS_IN_THIS__" },
    { emoji: "📋", label: "Snap a sign-up sheet", prompt: "__OPEN_SIGNUP_SNAP__" },
    { emoji: "🩹", label: "Snap a rash", prompt: "__OPEN_RASH_SNAP__" },
    { emoji: "🎁", label: "Gift ideas", prompt: "I need gift ideas for someone and I have no clue what to get them." },
    { emoji: "🧳", label: "Pack for the weekend", prompt: "Help me pack for a quick weekend trip." },
    { emoji: "🏫", label: "School week prep", prompt: "Help me get ahead of next week." },
    { emoji: "🎂", label: "Plan a kid birthday", prompt: "I'm planning a birthday party. Help me figure it out." },
    { emoji: "📋", label: "Make a quick checklist", prompt: "I have a thing happening soon. Help me make a checklist." },
    { emoji: "🧠", label: "Brain dump", prompt: "My brain is full and I just need to get everything out." },
    { emoji: "🥗", label: "Lunch ideas for the week", prompt: "Help me figure out lunches for the week." },
    { emoji: "🤖", label: "AI 101, where do I start?", prompt: "I keep hearing about AI but I have no idea where to start." },
    { emoji: "✈️", label: "Plan a family trip", prompt: "Help me plan a family-friendly trip on a real budget." },
    getEventPlannerChip(),
  ],
  "Homestead / DIY Mom": [
    getGiftTrackerChip(),
    { emoji: "🌱", label: "Garden planning", prompt: "I want to start a garden but I have no idea where to begin." },
    { emoji: "🍅", label: "Help me preserve", prompt: "I want to preserve some of what I'm growing. I've never canned before." },
    { emoji: "🐔", label: "Backyard chicken help", prompt: "I have backyard chickens and I need help with something." },
    getRenoHelperChip(),
    { emoji: "🎨", label: "Paint or design help", prompt: "I'm trying to make a design decision for my house. Help me decide." },
    { emoji: "🌿", label: "Seasonal homestead plan", prompt: "Help me plan what I should be focusing on at the homestead this season." },
    { emoji: "🪴", label: "Snap your plant", prompt: "__OPEN_PLANT_SNAP__" },
    { emoji: "🔍", label: "What's in this?", prompt: "__OPEN_WHATS_IN_THIS__" },
    { emoji: "📋", label: "Snap a sign-up sheet", prompt: "__OPEN_SIGNUP_SNAP__" },
    { emoji: "🩹", label: "Snap a rash", prompt: "__OPEN_RASH_SNAP__" },
    getEventPlannerChip(),
  ],
  "Just Getting Started Mom": [
    getGiftTrackerChip(),
    { emoji: "💭", label: "Where do I start?", prompt: "I'm new to using AI and I'm not sure what to ask. Walk me through it." },
    { emoji: "💜", label: "Say something nice", prompt: "I'm having a hard day. Just say something kind to me." },
    { emoji: "📝", label: "Help me write a quick text", prompt: "I need to send a text and I don't know how to word it." },
    { emoji: "😮‍💨", label: "I need to vent", prompt: "I just need to vent for a second. Just listen." },
    { emoji: "🔍", label: "What's in this?", prompt: "__OPEN_WHATS_IN_THIS__" },
    { emoji: "📋", label: "Snap a sign-up sheet", prompt: "__OPEN_SIGNUP_SNAP__" },
    { emoji: "🩹", label: "Snap a rash", prompt: "__OPEN_RASH_SNAP__" },
    getEventPlannerChip(),
  ],
  "ai_skeptic": [
    getGiftTrackerChip(),
    { emoji: "✍️", label: "Help me think through this", prompt: "I'm trying to decide something and I want to think it through." },
    { emoji: "📚", label: "Explain this to me well", prompt: "Can you explain something to me? I want to actually understand it, not just get a summary." },
    { emoji: "💌", label: "Write a hard message", prompt: "I need to write a message about something difficult. Help me get the words right." },
    { emoji: "🧠", label: "Talk to my kids about this", prompt: "I need to talk to my kids about something hard. Can you help me figure out how to approach it?" },
    { emoji: "📖", label: "Build a learning plan", prompt: "I want to learn something new. Help me build a realistic plan." },
    { emoji: "🤨", label: "Is AI actually useful?", prompt: "I'm skeptical AI is actually useful for moms like me. Prove it: ask me 3 quick questions about my actual week, then show me one real thing you can take off my plate today." },
    { emoji: "📋", label: "Snap a sign-up sheet", prompt: "__OPEN_SIGNUP_SNAP__" },
    { emoji: "🩹", label: "Snap a rash", prompt: "__OPEN_RASH_SNAP__" },
  ],
  "new_mom": [
    getGiftTrackerChip(),
    { emoji: "🍼", label: "Why won't baby sleep", prompt: "Help me figure out what's going on with baby's sleep. Ask me how old baby is and when baby last ate before giving advice." },
    { emoji: "🤱", label: "Feeding troubleshooting", prompt: "Something feels off with feeding and I need help thinking it through. Ask me one thing first so you have context." },
    { emoji: "🤒", label: "Should I worry about this", prompt: "Something with baby is making me worry and I need a calm second opinion. Tell me what to monitor and when to call the pediatrician. Don't try to diagnose." },
    { emoji: "💭", label: "Brain dump", prompt: "My brain is full and I just need to get everything out." },
    { emoji: "😮‍💨", label: "I need to vent", prompt: "I just need to vent for a second. Just listen." },
    { emoji: "📅", label: "Plan my day around naps", prompt: "Help me plan a realistic day around baby's naps. Ask me what baby's nap schedule looks like before suggesting anything." },
    { emoji: "🛒", label: "Worth buying for baby?", prompt: "Cut through the Instagram-fueled overconsumption. What baby items do I actually need versus what's marketed to me?" },
    { emoji: "🧷", label: "Snap a diaper", prompt: "__OPEN_DIAPER_SNAP__" },
    { emoji: "🩹", label: "Snap a rash", prompt: "__OPEN_RASH_SNAP__" },
    { emoji: "🤖", label: "AI 101, where do I start?", prompt: "I keep hearing about AI but I have no idea where to start." },
  ],
};

export const MOM_TYPES = [
  { key: "new_mom", emoji: "🍼", title: "New Mom", description: "Baby's here. Sleep is gone. Help me figure out what's normal." },
  { key: "Corporate Mom", emoji: "🏢", title: "Corporate Mom", description: "Meetings, emails, leadership, and getting it all done before 5pm." },
  { key: "Creative / Side Hustle Mom", emoji: "✨", title: "Creative / Side Hustle Mom", description: "Building something on the side. Or trying to. Or thinking about it." },
  { key: "All-the-Things Mom", emoji: "🏃‍♀️", title: "All-the-Things Mom", description: "Carpools, meal plans, school stuff. You need answers fast." },
  { key: "Homestead / DIY Mom", emoji: "🌱", title: "Homestead / DIY Mom", description: "Garden, chickens, reno projects. Real life, real hands." },
  { key: "Just Getting Started Mom", emoji: "🌸", title: "Just Getting Started Mom", description: "New to AI. Want help, but don't want to feel overwhelmed." },
  { key: "ai_skeptic", emoji: "🪞", title: "AI Skeptic Mom", description: "You don't fully trust AI yet. That's smart. Let's earn it." },
] as const;

export function getRandomMealChip(momTypes?: string[]): ChipDef {
  if (momTypes && momTypes.includes("new_mom")) {
    return NEW_MOM_MEAL_CHIPS[Math.floor(Math.random() * NEW_MOM_MEAL_CHIPS.length)];
  }
  return MEAL_CHIPS[Math.floor(Math.random() * MEAL_CHIPS.length)];
}

/**
 * A random seasonal chip that is actually in season, or null when none is
 * (issue #593). Callers MUST handle null — the welcome grid drops the slot
 * and backfills with a personalized chip rather than showing a stale one.
 */
export function getRandomSeasonalChip(now: Date = new Date()): ChipDef | null {
  const inSeason = SEASONAL_CHIPS.filter((c) => isChipInSeason(c, now));
  if (inSeason.length === 0) return null;
  return inSeason[Math.floor(Math.random() * inSeason.length)];
}

export function getPersonalizedChips(momTypes: string[]): ChipDef[] {
  const pool = momTypes.length > 0
    ? momTypes.flatMap((t) => MOM_TYPE_POOLS[t] || [])
    : DEFAULT_CHIPS;

  const seen = new Set<string>();
  const unique: ChipDef[] = [];
  for (const chip of pool) {
    if (!seen.has(chip.label)) {
      seen.add(chip.label);
      unique.push(chip);
    }
  }

  for (let i = unique.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [unique[i], unique[j]] = [unique[j], unique[i]];
  }

  return unique.slice(0, 7);
}

/** Find any chip definition across all pools by its label. */
export function findChipByLabel(label: string): ChipDef | null {
  const all: ChipDef[] = [
    ...MEAL_CHIPS,
    ...NEW_MOM_MEAL_CHIPS,
    ...DEFAULT_CHIPS,
    ...SEASONAL_CHIPS,
    ...Object.values(MOM_TYPE_POOLS).flat(),
    // See-more-only chips (not in any pool) — must be resolvable so favorites work.
    getHowToMakeChip(),
  ];
  return all.find((c) => c.label === label) || null;
}

/**
 * Build the personalized slots respecting favorites/meal already shown.
 */
export function getPersonalizedChipsFiltered(
  momTypes: string[],
  excludeLabels: Set<string>,
  count: number
): ChipDef[] {
  if (count <= 0) return [];
  const pool = momTypes.length > 0
    ? momTypes.flatMap((t) => MOM_TYPE_POOLS[t] || [])
    : DEFAULT_CHIPS;

  const seen = new Set<string>(excludeLabels);
  const unique: ChipDef[] = [];
  for (const chip of pool) {
    if (!seen.has(chip.label)) {
      seen.add(chip.label);
      unique.push(chip);
    }
  }

  for (let i = unique.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [unique[i], unique[j]] = [unique[j], unique[i]];
  }
  return unique.slice(0, count);
}

export type ChipGroup = { title: string; emoji: string; chips: ChipDef[] };

/**
 * Curated category lists for the "See more ideas" expanded section.
 * Order here defines display order. Labels reference chips defined above.
 */
const SEE_MORE_CATEGORIES: { title: string; emoji: string; labels: string[] }[] = [
  {
    title: "Quick Wins",
    emoji: "🏃‍♀️",
    labels: [
      "What's in this?",
      "__HOW_TO_MAKE__",
      "Snap a rash",
      "Gift ideas",
      "Gift list tracker",
      "Pack for the weekend",
      "School week prep",
      "Make a quick checklist",
      "Brain dump",
      "Budget tracker",
      "Plan a kid birthday",
      "__EVENT_PLANNER__",
      "AI 101, where do I start?",
      "Plan a family trip",
    ],
  },
  {
    title: "Meal Ideas",
    emoji: "🍽️",
    labels: [
      "Meal prep for the week",
      "What's for dinner tonight",
      "Meal plan for picky eaters",
      "Lunch ideas for the week",
      "Snap your fridge or pantry",
    ],
  },
  {
  title: "Seasonal",
  emoji: "🌸",
  labels: [
    "Summer bucket list",
    "Summer camps & childcare",
    "Back-to-school game plan",
  ],
},
 {
    title: "New Mom",
    emoji: "🍼",
    labels: [
      "Why won't baby sleep",
      "Feeding troubleshooting",
      "Should I worry about this",
      "Plan my day around naps",
      "Worth buying for baby?",
    ],
  },
  {
    title: "Work & Career",
    emoji: "💼",
    labels: [
      "Write a work email",
      "Prep for a big meeting",
      "Handle a hard convo",
      "Make me look smart",
      "Manager meeting prep",
    ],
  },
  {
    title: "Home & Garden",
    emoji: "🌱",
    labels: [
      "Garden planning",
      "Snap your plant",
      "Help me preserve",
      "Backyard chicken help",
      "__RENO_HELPER__",
      "Paint or design help",
      "Seasonal homestead plan",
    ],
  },
  {
    title: "Creative & Side Hustle",
    emoji: "✨",
    labels: [
      "Restart my side project",
      "Write product descriptions",
      "Pick a business idea",
      "Pitch to a client",
      "Launch something this week",
      "Content for my brand",
    ],
  },
  {
    title: "Gentle Starts",
    emoji: "🌸",
    labels: [
      "Where do I start?",
      "Say something nice",
      "Help me write a quick text",
      "I need to vent",
    ],
  },
  {
    title: "AI Skeptic",
    emoji: "🪞",
    labels: [
      "Help me think through this",
      "Explain this to me well",
      "Write a hard message",
      "Talk to my kids about this",
      "Build a learning plan",
      "Is AI actually useful?",
    ],
  },
];

/** Talk to Kin chip — Pro-gated voice mode entry point. */
export function getTalkToKinChip(): ChipDef {
  return {
    emoji: "✦",
    label: "Talk to Kin",
    // Special action token — WelcomeScreen handles this to activate voice mode
    prompt: "__TALK_TO_KIN__",
    promptFree: "Tell me about voice mode — I tapped the Talk to Kin chip but I'm not a Pro subscriber yet.",
    isProFeature: true,
  };
}

/**
 * Build the "See more ideas" groups.
 *
 * Out-of-season chips are filtered here, at read time, off each chip's own
 * `months` window — there is no second list of "what's seasonal right now"
 * to keep in sync. A category left with zero chips is dropped entirely by the
 * `chips.length > 0` check below, so Seasonal never renders as a bare header.
 */
export function getSeeMoreChips(
  shownLabels: Set<string>,
  _currentMealChip: ChipDef,
  now: Date = new Date()
): ChipGroup[] {
  const groups: ChipGroup[] = [];
  for (const cat of SEE_MORE_CATEGORIES) {
    const chips: ChipDef[] = [];
    for (const label of cat.labels) {
      if (label === "__RENO_HELPER__") {
        // Skip if any rotating reno label is already in the main grid.
        const alreadyShown = Array.from(RENO_HELPER_LABEL_SET).some((l) =>
          shownLabels.has(l)
        );
        if (alreadyShown) continue;
        chips.push(getRenoHelperChip());
        continue;
      }
      if (label === "__EVENT_PLANNER__") {
        // Skip if any rotating event planner label is already in the main grid.
        const alreadyShown = Array.from(EVENT_PLANNER_LABEL_SET).some((l) =>
          shownLabels.has(l)
        );
        if (alreadyShown) continue;
        chips.push(getEventPlannerChip());
        continue;
      }
      if (label === "__HOW_TO_MAKE__") {
        // Lockstep sentinel — resolves to the "How do I make this?" chip.
        const chip = getHowToMakeChip();
        if (shownLabels.has(chip.label)) continue;
        chips.push(chip);
        continue;
      }
      if (shownLabels.has(label)) continue;
      const def = findChipByLabel(label);
      if (def && isChipInSeason(def, now)) chips.push(def);
    }
    if (chips.length > 0) {
      groups.push({ title: cat.title, emoji: cat.emoji, chips });
    }
  }
  return groups;
}
